import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  DialogContentText,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { inputStyle, primaryBtnStyle } from "../styles/styling";
import { useDispatch, useSelector } from "react-redux";
import { changePassword, verifyPassword } from "../redux/slices/authSlice";
import { AppDispatch, RootState } from "../redux/store";

interface PasswordResetProps {
  open: boolean;
  handleClose: () => void;
}

export const passReqColor = (isPassEmpty: boolean, isReqMet: boolean) => {
  if (isPassEmpty) {
    return "black";
  }
  if (!isPassEmpty && !isReqMet) {
    return "red";
  }
  if (!isPassEmpty && isReqMet) {
    return "green";
  }
};

const ResetPassword: React.FC<PasswordResetProps> = ({ open, handleClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    currentUser,
    passwordChangeStatus: status,
    passwordChangeError,
  } = useSelector((state: RootState) => state.auth);
  const [resetPassStep, setResetPassStep] = useState<1 | 2>(1);

  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(true);

  const [showCurrentPassword, setShowCurrentPassword] =
    useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] =
    useState<boolean>(false);

  const [incorrectCurrentPass, setIncorrectCurrentPass] =
    useState<boolean>(false);

  const [isAtLeast8Char, setIsAtleast8Char] = useState<boolean>(false);
  const [hasNum, setHasNum] = useState<boolean>(false);
  const [hasSpecialChar, setHasSpecialChar] = useState<boolean>(false);
  const [hasUpperCase, setHasUpperCase] = useState<boolean>(false);
  const [hasLowerCase, setHasLowerCase] = useState<boolean>(false);
  const [passwordMatched, setPasswordMatch] = useState<boolean>(false);

  const changePassDisabled =
    !isAtLeast8Char ||
    !hasLowerCase ||
    !hasNum ||
    !hasSpecialChar ||
    !hasUpperCase ||
    !passwordMatched;

  const onChangePass = () => {
    setIsEditing(false);
    dispatch(
      changePassword({
        userId: currentUser?.id ?? "",
        password: newPassword,
      })
    )
      .unwrap()
      .then(() => {
        handleClose();
        setIsEditing(false);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (newPassword.trim()) {
      setIsAtleast8Char(newPassword.trim().length >= 8);
      setHasNum(/[0-9]/g.test(newPassword));
      setHasSpecialChar(/[!-/:-@[-`{-~]/.test(newPassword));
      setHasUpperCase(/[A-Z]/.test(newPassword));
      setHasLowerCase(/[a-z]/.test(newPassword));
    }
  }, [newPassword]);

  useEffect(() => {
    if (confirmNewPassword.trim()) {
      setPasswordMatch(newPassword === confirmNewPassword);
    }
  }, [confirmNewPassword, newPassword]);

  return (
    <Dialog open={open}>
      <Box
        sx={{ width: "520px", height: resetPassStep === 2 ? "510px" : "270px" }}
      >
        {resetPassStep === 1 && (
          <>
            <DialogTitle>Update Default Password</DialogTitle>
            <DialogContent>
              <DialogContentText>
                For your security, as a first-time user, you are required to
                change your default password.
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                label="Current Password"
                type={showCurrentPassword ? "text" : "password"}
                fullWidth
                variant="outlined"
                value={currentPassword}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setIncorrectCurrentPass(false);
                  setCurrentPassword(event.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && currentPassword.trim()) {
                    dispatch(
                      verifyPassword({
                        userId: currentUser?.id ?? "",
                        password: currentPassword,
                      })
                    )
                      .unwrap()
                      .then(() => {
                        setResetPassStep(2);
                      })
                      .catch(() => {
                        setIncorrectCurrentPass(true);
                      });
                  }
                }}
                sx={inputStyle()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setShowCurrentPassword(!showCurrentPassword);
                        }}
                        edge="end"
                      >
                        {showCurrentPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {incorrectCurrentPass && (
                <Typography color={"red"}>Incorrect Password</Typography>
              )}
              <DialogActions>
                <Button
                  onClick={() => {
                    dispatch(
                      verifyPassword({
                        userId: currentUser?.id ?? "",
                        password: currentPassword,
                      })
                    )
                      .unwrap()
                      .then(() => {
                        setResetPassStep(2);
                      })
                      .catch(() => {
                        setIncorrectCurrentPass(true);
                      });
                  }}
                  color="primary"
                  type="submit"
                  sx={primaryBtnStyle}
                  disabled={!currentPassword.trim()}
                >
                  {status === "loading" ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Submit"
                  )}
                </Button>
              </DialogActions>
            </DialogContent>
          </>
        )}
        {resetPassStep === 2 && (
          <>
            <DialogTitle>Enter New Password</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Upon successful password update, you will be logged out
                automatically. Please log in again using your new password to
                continue.
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                fullWidth
                variant="outlined"
                value={newPassword}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setNewPassword(event.target.value);
                  setIsEditing(true);
                }}
                sx={inputStyle()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !changePassDisabled) {
                    onChangePass();
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setShowNewPassword(!showNewPassword);
                        }}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                color={passReqColor(
                  newPassword.trim().length === 0,
                  isAtLeast8Char
                )}
              >
                • Must have at least 8 characters*
              </Typography>
              <Typography
                color={passReqColor(
                  newPassword.trim().length === 0,
                  hasUpperCase
                )}
              >
                • Must have at least 1 uppercase letter*
              </Typography>
              <Typography
                color={passReqColor(
                  newPassword.trim().length === 0,
                  hasLowerCase
                )}
              >
                • Must have at least 1 lowercase letter*
              </Typography>
              <Typography
                color={passReqColor(newPassword.trim().length === 0, hasNum)}
              >
                • Must have at least 1 number*
              </Typography>
              <Typography
                color={passReqColor(
                  newPassword.trim().length === 0,
                  hasSpecialChar
                )}
              >
                • Must have at least 1 special character*
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Confirm New Password"
                type={showConfirmNewPassword ? "text" : "password"}
                fullWidth
                variant="outlined"
                value={confirmNewPassword}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setConfirmNewPassword(event.target.value);
                  setIsEditing(true);
                }}
                sx={inputStyle()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !changePassDisabled) {
                    onChangePass();
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setShowConfirmNewPassword(!showConfirmNewPassword);
                        }}
                        edge="end"
                      >
                        {showConfirmNewPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {confirmNewPassword.trim() && !passwordMatched && (
                <Typography color={"red"}>Passwords do not match.</Typography>
              )}
              {passwordChangeError && !isEditing && (
                <Typography color={"red"}>{passwordChangeError}</Typography>
              )}
              <DialogActions>
                <Button
                  color="primary"
                  type="submit"
                  sx={primaryBtnStyle}
                  disabled={changePassDisabled}
                  onClick={() => {
                    onChangePass();
                  }}
                >
                  {status === "loading" ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Submit"
                  )}
                </Button>
              </DialogActions>
            </DialogContent>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default ResetPassword;
